import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: the waiting lists of every BBSO course that has the setting ON.
// Each course shows its queue oldest-first; tick one or several rows and
// register them. `payment` works exactly like the roster screen:
//   plata      → pay_course reserves the seat + sends the Stripe payment mail;
//                hourly/sessions become active + get the welcome mail.
//   fara_plata → active right away for any type, no payment.
// Promotion is per row on the backend, so registering more people than there
// are free seats registers what fits and leaves the rest queued.

export function AdminWaitlistPage() {
  const { t } = useTranslation()
  const { user: me, loading: authLoading } = useAuth()

  const [courses, setCourses] = useState([])
  const [picked, setPicked] = useState({})   // enrollment_id → true
  const [payment, setPayment] = useState({}) // course_id → 'plata' | 'fara_plata'
  const [results, setResults] = useState({}) // enrollment_id → { ok, reason }
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function reload() {
    api.admin.waitlist.list()
      .then((d) => setCourses(d.courses || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me])

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }))

  function toggleAll(course) {
    const ids = course.entries.map((e) => e.id)
    const allOn = ids.length > 0 && ids.every((i) => picked[i])
    setPicked((p) => {
      const next = { ...p }
      ids.forEach((i) => { next[i] = !allOn })
      return next
    })
  }

  async function promote(course) {
    const ids = course.entries.map((e) => e.id).filter((i) => picked[i])
    if (ids.length === 0) return
    setBusy(true); setError(null)
    try {
      const d = await api.admin.waitlist.promote({
        enrollment_ids: ids,
        payment: payment[course.id] || 'plata',
      })
      const map = {}
      for (const r of d.results || []) map[r.enrollment_id] = r
      setResults((prev) => ({ ...prev, ...map }))
      // Clear only the rows that actually went through; failures stay ticked
      // so the admin can retry them.
      setPicked((p) => {
        const next = { ...p }
        for (const r of d.results || []) if (r.ok) delete next[r.enrollment_id]
        return next
      })
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusy(false) }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_waitlist.title')}</h1>
      <p className="muted">{t('admin_waitlist.intro')}</p>

      {error && (
        <p className="error">
          {t(`admin_waitlist.errors.${error}`, {
            defaultValue: t(`admin_courses.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      {courses.length === 0 && <p className="muted">{t('admin_waitlist.empty_state')}</p>}

      {courses.map((c) => {
        const ids = c.entries.map((e) => e.id)
        const selected = ids.filter((i) => picked[i]).length
        return (
          <section key={c.id} className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>
              <Link to={`/admin/courses/${c.id}/edit`}>{c.name}</Link>
              {c.active !== 1 && (
                <span className="badge muted-badge" style={{ marginLeft: '0.5rem' }}>
                  {t('admin_waitlist.inactive')}
                </span>
              )}
            </h2>
            <p className="muted small">
              {t(`admin_courses.type_${c.type}`, { defaultValue: c.type })}
              {' · '}
              {c.capacity === null
                ? t('admin_waitlist.no_capacity')
                : t('admin_waitlist.seats', {
                    enrolled: c.enrolled_count, capacity: c.capacity, free: c.seats_left,
                  })}
              {' · '}
              {t('admin_waitlist.queued', { count: c.entries.length })}
            </p>

            {c.entries.length === 0 && <p className="muted">{t('admin_waitlist.no_entries')}</p>}

            {c.entries.length > 0 && (
              <>
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          aria-label={t('admin_waitlist.select_all')}
                          checked={ids.length > 0 && ids.every((i) => picked[i])}
                          onChange={() => toggleAll(c)}
                        />
                      </th>
                      <th>{t('teacher.col_participant')}</th>
                      <th>{t('teacher.col_account')}</th>
                      <th>{t('admin_waitlist.col_since')}</th>
                      <th>{t('admin_waitlist.col_result')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.entries.map((e) => {
                      const r = results[e.id]
                      return (
                        <tr key={e.id}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={e.participant_name}
                              checked={!!picked[e.id]}
                              onChange={() => toggle(e.id)}
                            />
                          </td>
                          <td>{e.participant_name}</td>
                          <td>
                            {e.account_name}<br />
                            <span className="muted small">{e.account_email}</span>
                          </td>
                          <td className="muted small">{String(e.enrolled_at || '').slice(0, 16)}</td>
                          <td>
                            {r && r.ok && (
                              <span className="badge">
                                {t(`enrollments.status_${r.status}`, { defaultValue: r.status })}
                              </span>
                            )}
                            {r && !r.ok && (
                              <span className="error">
                                {t(`admin_waitlist.reasons.${r.reason}`, { defaultValue: r.reason })}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                <div className="form-actions" style={{ alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{ margin: 0 }}>
                    <span>{t('admin_courses.roster_form_payment')}</span>
                    <select
                      value={payment[c.id] || 'plata'}
                      onChange={(ev) => setPayment((p) => ({ ...p, [c.id]: ev.target.value }))}
                    >
                      <option value="plata">{t('admin_courses.payment_plata')}</option>
                      <option value="fara_plata">{t('admin_courses.payment_fara_plata')}</option>
                    </select>
                  </label>
                  <button type="button" disabled={busy || selected === 0} onClick={() => promote(c)}>
                    {busy
                      ? t('common.loading')
                      : t('admin_waitlist.promote', { count: selected })}
                  </button>
                </div>
              </>
            )}
          </section>
        )
      })}
    </main>
  )
}
